import * as THREE from 'three'
import {
  defaultAnchorSpec,
  PoseSmoother,
  solveNecklace,
  type AnchorSpec,
  type CameraModel,
  type FaceFrame,
  type NecklaceSolveResult,
  type POSE_SMOOTHER_PRESETS,
  type PoseFrame,
  type PoseSmootherConfig,
} from '@ar-suite/core'
import { applyPose, ARScene, applyMaterialOverrides, disposeObject, FaceOccluder } from '@ar-suite/render-web'

export interface NecklaceModuleOptions {
  spec?: AnchorSpec
  smoothing?: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS
}

/** Necklace module: face + shoulder-pose solver (core) → smoother → three.js. Neck occluder hides the chain's back. */
export class NecklaceModule {
  readonly root = new THREE.Group()
  readonly smoother: PoseSmoother
  private _spec: AnchorSpec
  private model: THREE.Object3D | null = null
  private result: NecklaceSolveResult | null = null
  private occluder: FaceOccluder | null = null

  constructor(readonly scene: ARScene, options: NecklaceModuleOptions = {}) {
    this._spec = options.spec ?? defaultAnchorSpec('necklace')
    // necklaces swing a little but mostly sit still: favour stability
    this.smoother = new PoseSmoother(options.smoothing ?? 'stable')
    this.root.name = 'necklace'
    this.root.visible = false
    scene.content.add(this.root)
  }

  get spec(): AnchorSpec {
    return this._spec
  }
  get lastResult(): NecklaceSolveResult | null {
    return this.result
  }
  get hasModel(): boolean {
    return this.model !== null
  }
  set visible(v: boolean) {
    this.root.visible = v
  }

  attachOccluder(occluder: FaceOccluder): void {
    this.occluder = occluder
    occluder.setSpec(this._spec.occluder)
  }
  setSpec(spec: AnchorSpec): void {
    this._spec = spec
    this.occluder?.setSpec(spec.occluder)
  }
  patchSpec(patch: Partial<AnchorSpec>): void {
    Object.assign(this._spec, patch)
    this.setSpec(this._spec)
  }
  setSmoothing(cfg: PoseSmootherConfig | keyof typeof POSE_SMOOTHER_PRESETS): void {
    this.smoother.configure(cfg)
  }

  setModel(model: THREE.Object3D | null): void {
    if (this.model) {
      this.root.remove(this.model)
      disposeObject(this.model)
    }
    this.model = model
    if (model) this.root.add(model)
  }

  applyVariantMaterials(overrides: Record<string, { color?: string; metalness?: number; roughness?: number; opacity?: number }>): void {
    if (this.model) applyMaterialOverrides(this.model, overrides)
  }

  update(frame: FaceFrame, cam: CameraModel, tMs: number, poseFrame?: PoseFrame | null): NecklaceSolveResult {
    this.result = solveNecklace(frame, cam, this._spec, poseFrame, undefined, this.result ?? undefined)
    const smoothed = this.smoother.update(this.result.pose, tMs)
    applyPose(this.root, smoothed)
    this.root.visible = true
    return this.result
  }

  onTrackingLost(): void {
    this.root.visible = false
    this.smoother.reset()
  }

  dispose(): void {
    this.setModel(null)
    this.scene.content.remove(this.root)
  }
}
